import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Eye, 
  Brain, 
  ArrowRightLeft, 
  BarChart3, 
  FileText, 
  Anchor, 
  Trees, 
  Droplets, 
  Sprout, 
  Shield, 
  Activity, 
  FileCheck, 
  TrendingUp 
} from 'lucide-react';

const biomes = {
  marine: {
    id: 'marine',
    label: 'Marine/Coastal',
    status: 'operational', // operational | framework
    statusLabel: 'Live POC · Gulf of California & MARIS',
    stages: {
      observation: {
        title: 'Observation',
        desc: 'Visual fish census (LTEM), habitat mapping, satellite imagery.',
        tier: 'T2',
        tierDesc: 'Contextual · Credible institutional data (26 years)',
        uncertainty: 'Low'
      },
      interpretation: {
        title: 'Interpretation',
        desc: 'Reef Status Index, trophic structure analysis.',
        tier: 'T2',
        tierDesc: 'Contextual · Peer-reviewed methodology applied to site',
        uncertainty: 'Low-Med'
      },
      translation: {
        title: 'Translation',
        desc: 'Ecosystem service valuation (6 categories), Monte Carlo uncertainty.',
        tier: 'T3',
        tierDesc: 'Proposed · Methodology specified, validation pending',
        uncertainty: 'Medium'
      },
      metric: {
        title: 'Metric',
        desc: 'Evidence completeness, dependency summaries and explicit assessment boundaries.',
        tier: 'T3',
        tierDesc: 'Proposed · Outputs for evidence review',
        uncertainty: 'Med-High'
      },
      instrument: {
        title: 'Instrument',
        desc: 'Decision support for blue bonds, conservation finance due diligence.',
        tier: 'T4',
        tierDesc: 'Illustrative · Phase 0 Roadmap item',
        uncertainty: 'High'
      }
    }
  },
  forest: {
    id: 'forest',
    label: 'Forest',
    status: 'framework',
    statusLabel: 'Framework Design',
    stages: {
      observation: { title: 'Observation', desc: 'LiDAR canopy structure, camera traps, acoustic monitoring.', tier: 'T4', tierDesc: 'Design Capacity', uncertainty: 'Low' },
      interpretation: { title: 'Interpretation', desc: 'Forest structure indices, species occupancy, connectivity.', tier: 'T4', tierDesc: 'Design Capacity', uncertainty: 'Low-Med' },
      translation: { title: 'Translation', desc: 'Carbon stock valuation, watershed services, timber yield.', tier: 'T4', tierDesc: 'Design Capacity', uncertainty: 'Medium' },
      metric: { title: 'Metric', desc: 'Condition ratings, risk indices, data quality classification.', tier: 'T4', tierDesc: 'Design Capacity', uncertainty: 'Med-High' },
      instrument: { title: 'Instrument', desc: 'Forest bonds, REDD+ verification, payment for services.', tier: 'T4', tierDesc: 'Design Capacity', uncertainty: 'High' }
    }
  },
  freshwater: {
    id: 'freshwater',
    label: 'Freshwater',
    status: 'framework',
    statusLabel: 'Framework Design',
    stages: {
      observation: { title: 'Observation', desc: 'Water quality sensors, fish surveys, macroinvertebrates.', tier: 'T4', tierDesc: 'Design Capacity', uncertainty: 'Low' },
      interpretation: { title: 'Interpretation', desc: 'Trophic health indices, riparian integrity, flow connectivity.', tier: 'T4', tierDesc: 'Design Capacity', uncertainty: 'Low-Med' },
      translation: { title: 'Translation', desc: 'Purification value, fishery productivity, flood attenuation.', tier: 'T4', tierDesc: 'Design Capacity', uncertainty: 'Medium' },
      metric: { title: 'Metric', desc: 'Watershed ratings, resilience scores, service ranges.', tier: 'T4', tierDesc: 'Design Capacity', uncertainty: 'Med-High' },
      instrument: { title: 'Instrument', desc: 'Water funds, wetland credits, municipal bonds.', tier: 'T4', tierDesc: 'Design Capacity', uncertainty: 'High' }
    }
  },
  grassland: {
    id: 'grassland',
    label: 'Grassland',
    status: 'framework',
    statusLabel: 'Framework Design',
    stages: {
      observation: { title: 'Observation', desc: 'Drone imagery, soil sampling, grazing monitoring.', tier: 'T4', tierDesc: 'Design Capacity', uncertainty: 'Low' },
      interpretation: { title: 'Interpretation', desc: 'Vegetation structure, soil carbon density, herbivore dynamics.', tier: 'T4', tierDesc: 'Design Capacity', uncertainty: 'Low-Med' },
      translation: { title: 'Translation', desc: 'Sequestration rates, grazing productivity, erosion control.', tier: 'T4', tierDesc: 'Design Capacity', uncertainty: 'Medium' },
      metric: { title: 'Metric', desc: 'Rangeland condition ratings, degradation risk.', tier: 'T4', tierDesc: 'Design Capacity', uncertainty: 'Med-High' },
      instrument: { title: 'Instrument', desc: 'Regenerative ag finance, soil credits, easements.', tier: 'T4', tierDesc: 'Design Capacity', uncertainty: 'High' }
    }
  }
};

const evidenceTiers = {
  'T1': { color: 'bg-green-500', border: 'border-green-500', label: 'Verified', full: 'Peer-reviewed sources with DOI' },
  'T2': { color: 'bg-blue-500', border: 'border-blue-500', label: 'Contextual', full: 'Credible institutional data' },
  'T3': { color: 'bg-amber-500', border: 'border-amber-500', label: 'Proposed', full: 'Methodology specified, pending' },
  'T4': { color: 'bg-slate-500', border: 'border-slate-500', label: 'Illustrative', full: 'Placeholder for scoping' }
};

const pipelines = [
  { id: 'evidence', title: 'Evidence Register', desc: 'Sources, evidence tiers and current limits recorded in BlueGraph', status: 'operational', icon: FileCheck },
  { id: 'trace', title: 'Decision Trace', desc: 'Published paths connecting place, evidence and bounded review questions', status: 'operational', icon: Activity },
  { id: 'monitoring', title: 'Monitoring Design', desc: 'Observation priorities and validation requirements for a defined scope', status: 'roadmap', icon: Shield },
  { id: 'scenario', title: 'Scenario Assessment', desc: 'Bounded comparisons subject to domain-specific validation', status: 'roadmap', icon: TrendingUp }
];

const standards = [
  { name: 'Darwin Core / OBIS', desc: 'Biodiversity Data Structure' },
  { name: 'TNFD LEAP', desc: 'Financial Disclosure' },
  { name: 'SEEA-EA', desc: 'Ecosystem Accounting' },
  { name: 'PROV-O', desc: 'Data Provenance' }
];

const TranslationFramework = () => {
  const [activeBiome, setActiveBiome] = useState('marine');
  const [hoveredNode, setHoveredNode] = useState(null);
  const [expandedNode, setExpandedNode] = useState(null);
  
  const currentData = biomes[activeBiome];

  // Helper to handle expansion (one at a time)
  const handleNodeClick = (key) => {
    setExpandedNode(expandedNode === key ? null : key);
  };

  return (
    <div className="w-full">
        {/* Biome Selector */}
        <div className="flex justify-center mb-10 pb-4">
          <div className="grid grid-cols-2 sm:flex bg-abyss-900/50 p-1 rounded-sm border border-abyss-800">
            {Object.values(biomes).map((biome) => (
              <button
                key={biome.id}
                type="button"
                data-testid={`method-biome-${biome.id}`}
                aria-pressed={activeBiome === biome.id}
                onClick={() => { setActiveBiome(biome.id); setExpandedNode(null); }}
                className={`
                  flex items-center justify-center gap-2 px-4 py-3 rounded-sm text-xs font-mono uppercase transition-colors
                  ${activeBiome === biome.id 
                    ? 'bg-teal-500/10 text-teal-400 border border-teal-500/30' 
                    : 'text-sand-50/40 hover:text-sand-50/60 border border-transparent'}
                `}
              >
                {biome.id === 'marine' ? <Anchor size={12} /> : 
                 biome.id === 'forest' ? <Trees size={12} /> :
                 biome.id === 'freshwater' ? <Droplets size={12} /> : <Sprout size={12} />}
                {biome.label}
                {biome.id === 'marine' && <span className="ml-1 w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse"></span>}
              </button>
            ))}
          </div>
        </div>

        {/* Status Banner */}
        <div className="flex justify-center mb-8">
            <span data-testid="method-biome-status" className={`text-[10px] font-mono uppercase tracking-widest px-3 py-1 rounded border ${
                currentData.status === 'operational' 
                ? 'bg-teal-500/10 text-teal-400 border-teal-500/30' 
                : 'bg-slate-500/10 text-slate-400 border-slate-500/30'
            }`}>
                {currentData.statusLabel}
            </span>
        </div>

        {/* VISUALIZATION CHAIN */}
        <div className="relative mb-24">
            
            {/* Uncertainty Ribbon (Background) */}
            <div className="absolute inset-0 top-1/2 -translate-y-1/2 h-32 pointer-events-none opacity-20">
                <svg width="100%" height="100%" preserveAspectRatio="none" viewBox="0 0 100 100">
                    <defs>
                        <linearGradient id="uncertainty-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#2DD4BF" stopOpacity="0.05" />
                            <stop offset="100%" stopColor="#2DD4BF" stopOpacity="0.2" />
                        </linearGradient>
                    </defs>
                    <path d="M0,45 L100,20 L100,80 L0,55 Z" fill="url(#uncertainty-grad)" />
                </svg>
            </div>

            {/* Nodes Container */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 relative items-start">
                {/* Connecting Line (Desktop) */}
                <div className="hidden md:block absolute top-12 left-0 right-0 h-0.5 bg-gradient-to-r from-abyss-800 via-teal-900 to-abyss-800 -z-10" />

                {Object.entries(currentData.stages).map(([key, stage], index) => (
                    <ChainNode 
                        key={key}
                        stageKey={key}
                        data={stage}
                        index={index}
                        isHovered={hoveredNode === key}
                        isExpanded={expandedNode === key}
                        onHover={() => setHoveredNode(key)}
                        onLeave={() => setHoveredNode(null)}
                        onClick={() => handleNodeClick(key)}
                    />
                ))}
            </div>

            {/* Provenance Line */}
            <div className="hidden md:flex justify-between mt-8 px-12 border-t border-dashed border-teal-500/20 pt-2 text-[10px] font-mono text-teal-500/40 uppercase tracking-widest">
                <span>Traceable</span>
                <span>Queryable</span>
                <span>Auditable</span>
            </div>
        </div>

        {/* Output Pipelines */}
        <div className="grid md:grid-cols-4 gap-6 mb-24">
            {pipelines.map((pipe) => (
                <div 
                    key={pipe.id}
                    data-testid={`method-output-${pipe.id}`}
                    className={`
                        p-6 border rounded-sm transition-all group
                        ${pipe.status === 'operational' 
                            ? 'bg-abyss-900/30 border-teal-500/20 hover:border-teal-500/40' 
                            : 'bg-abyss-900/10 border-abyss-800 border-dashed hover:border-abyss-700'}
                    `}
                >
                    <div className="flex items-center justify-between mb-4">
                        <pipe.icon size={20} className={pipe.status === 'operational' ? 'text-teal-400' : 'text-slate-500'} />
                        {pipe.status === 'operational' && (
                            <span className="w-1.5 h-1.5 rounded-full bg-teal-400"></span>
                        )}
                    </div>
                    <h4 className={`text-sm font-medium mb-2 ${pipe.status === 'operational' ? 'text-sand-50' : 'text-sand-50/50'}`}>
                        {pipe.title}
                    </h4>
                    <p className="text-xs text-sand-50/50 leading-relaxed">
                        {pipe.desc}
                    </p>
                    {pipe.status === 'roadmap' && (
                        <div className="mt-3 text-[9px] uppercase tracking-widest text-amber-500/60 font-mono">
                            Design stage
                        </div>
                    )}
                </div>
            ))}
        </div>

        {/* Standards Alignment */}
        <div className="border-t border-abyss-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-8 opacity-60">
            <span className="text-xs font-mono uppercase tracking-widest text-sand-50/30">Reference frameworks</span>
            <div className="flex flex-wrap justify-center gap-8 md:gap-12">
                {standards.map((std) => (
                    <div key={std.name} className="text-center group cursor-default">
                        <div className="text-sm font-serif text-sand-50/60 group-hover:text-teal-400/80 transition-colors">{std.name}</div>
                        <div className="text-[9px] font-mono uppercase text-sand-50/20 group-hover:text-teal-500/40 transition-colors">{std.desc}</div>
                    </div>
                ))}
            </div>
        </div>
    </div>
  );
};

const ChainNode = ({ stageKey, data, index, isHovered, isExpanded, onHover, onLeave, onClick }) => {
    const icons = {
        observation: Eye,
        interpretation: Brain,
        translation: ArrowRightLeft,
        metric: BarChart3,
        instrument: FileText
    };
    const Icon = icons[stageKey];
    const tierInfo = evidenceTiers[data.tier];

    return (
        <div 
            className="relative group cursor-pointer h-full flex flex-col" // h-full allows grid stretch
            onMouseEnter={onHover}
            onMouseLeave={onLeave}
        >
            <button type="button" data-testid={`method-stage-${stageKey}`} aria-expanded={isExpanded} aria-controls={`method-detail-${stageKey}`} onClick={onClick} className={`text-left
                w-full rounded-lg p-4 md:p-6 flex flex-col justify-between
                transition-all duration-300 border backdrop-blur-sm
                ${isHovered || isExpanded
                    ? 'bg-gradient-to-br from-abyss-800 to-teal-900/20 border-teal-500/50 translate-y-[-4px] shadow-lg shadow-teal-900/20' 
                    : 'bg-abyss-900/40 border-abyss-700/50'}
                 min-h-[8rem] 
            `}>
                <div className="flex justify-between items-start mb-4">
                    <Icon size={20} className={isHovered || isExpanded ? 'text-teal-400' : 'text-sand-50/40'} />
                    <div className="relative group/tier">
                        <div title={tierInfo.full} className={`text-[9px] font-mono px-1.5 py-0.5 rounded border ${tierInfo.border} ${tierInfo.color} bg-opacity-10 text-opacity-90 text-sand-50 cursor-help`}>
                            {data.tier} · {tierInfo.label}
                        </div>
                    </div>
                </div>
                
                <div className="mt-auto">
                    <div className="text-[10px] font-mono text-teal-500/50 uppercase tracking-widest mb-1">Step 0{index + 1}</div>
                    <div className={`font-serif text-lg leading-tight ${isHovered || isExpanded ? 'text-white' : 'text-sand-50/80'}`}>{data.title}</div>
                </div>
            </button>

            {/* Description Dropdown (Push content down) */}
            <AnimatePresence>
                {isExpanded && (
                    <motion.div 
                        id={`method-detail-${stageKey}`}
                        data-testid={`method-detail-${stageKey}`}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="mt-2 w-full p-3 bg-abyss-950/80 border border-teal-500/20 rounded relative z-10 flex flex-col items-center justify-center shadow-lg overflow-hidden"
                    >
                        <p 
                            className="text-[10px] text-sand-50/90 leading-relaxed font-light mb-2 text-center w-full"
                        >
                            {data.desc}
                        </p>
                        <div className="w-full flex flex-col items-center gap-1 text-[8px] font-mono text-sand-50/40 border-t border-abyss-800 pt-2">
                            <span className="uppercase tracking-widest text-teal-500/50 text-center">Evidence Tier</span>
                            <span className="text-center">{data.tierDesc}</span>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default TranslationFramework;
